import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { COLLECTIONS } from "@/data/products";
import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";

/**
 * Featured Collections — horizontal scrolling editorial collection cards.
 */
const FeaturedCollections = () => {
  const scrollRef = useRef(null);

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.clientWidth * 0.85;
    scrollRef.current.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  return (
    <section
      className="py-16 lg:py-24"
      style={{ background: "#FDF6F0" }}
      data-testid="featured-collections"
    >
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
        <div className="flex items-end justify-between gap-5 mb-10 lg:mb-12">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="gold-divider" />
              <span className="overline">Curated Edits</span>
            </div>
            <h2
              className="font-serif text-plum leading-[1] tracking-tight"
              style={{ fontSize: "clamp(32px, 4.4vw, 56px)" }}
            >
              Featured <span className="italic text-blush">collections</span>
            </h2>
            <p className="mt-3 text-plum/65 max-w-[460px] text-[15px]">
              Pieces grouped by mood, moment & story — slide to explore.
            </p>
          </div>
          <div className="hidden lg:flex items-center gap-2">
            <button
              onClick={() => scroll("left")}
              className="w-11 h-11 rounded-full border border-gold/40 text-plum flex items-center justify-center hover:bg-gold hover:text-cream hover:border-gold transition-colors"
              aria-label="Scroll left"
              data-testid="collections-scroll-left"
            >
              <ChevronLeft size={17} />
            </button>
            <button
              onClick={() => scroll("right")}
              className="w-11 h-11 rounded-full border border-gold/40 text-plum flex items-center justify-center hover:bg-gold hover:text-cream hover:border-gold transition-colors"
              aria-label="Scroll right"
              data-testid="collections-scroll-right"
            >
              <ChevronRight size={17} />
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-5 lg:gap-7 overflow-x-auto no-scrollbar snap-x-mobile -mx-5 lg:mx-0 px-5 lg:px-0 pb-4"
        >
          {COLLECTIONS.map((c) => (
            <Link
              key={c.id}
              to={`/shop?collection=${c.id}`}
              className="group flex-shrink-0 w-[260px] sm:w-[320px] lg:w-[360px] relative overflow-hidden rounded-[24px] aspect-[4/5]"
              style={{ background: c.color }}
              data-testid={`collection-${c.id}`}
            >
              <img
                src={c.image}
                alt={c.name}
                className="absolute inset-0 w-full h-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />
              <div
                className="absolute inset-0"
                style={{ background: `linear-gradient(180deg, transparent 30%, ${c.color}EE 95%)` }}
              />
              <div className="relative h-full flex flex-col justify-end p-6 lg:p-7">
                <p
                  className="text-[10px] tracking-[0.32em] uppercase mb-2 font-medium opacity-85"
                  style={{ color: c.text }}
                >
                  {c.count}
                </p>
                <h3
                  className="font-serif leading-none mb-2"
                  style={{ color: c.text, fontSize: "clamp(24px, 2.4vw, 34px)" }}
                >
                  {c.name}
                </h3>
                <p className="text-[13px] mb-4" style={{ color: c.text, opacity: 0.85 }}>
                  {c.descriptor}
                </p>
                <span
                  className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase font-medium w-fit transition-all group-hover:gap-3"
                  style={{ color: c.text }}
                >
                  Shop edit <ArrowUpRight size={12} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedCollections;
